import React, {useEffect, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import './index.css';
import styled from "@emotion/styled";
import axios from "axios";
import {getConfig} from "../../AppConfig";
import {IonBackdrop, IonButton, IonCol, IonGrid, IonIcon, IonItem, IonLabel, IonRow, IonToast} from "@ionic/react";
import {createOutline, lockClosed, closeOutline, chevronBackOutline} from "ionicons/icons";
import Text from "../CommonStyles/Text";
import CurrencyInput from "react-currency-input-field";

interface Params {
  eventcode: string
  id: string
}

export const Input = styled("input")`
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  height: 60px;
  border-radius: 10px;
  background-color: #e0ebf180;
  outline: none;
  border: 1px solid #66666660;
  padding-left: 15px;
  ::placeholder {
    font-size: 14px;
    padding-left: 0px;
  }
`;

const Select = styled("select")`
  display: flex;
  justify-content: space-between;
  box-sizing: border-box;
  height: 60px;
  border-radius: 10px;
  background-color: #e0ebf180;
  outline: none;
  border: 1px solid #66666660;
  padding-left: 15px;

  ::placeholder {
    font-size: 14px;
    padding-left: 0px;
  }
  &:disabled {
    cursor: not-allowed;
  }
`

const Option = styled("option")`
  box-sizing: border-box;
  outline: none;
  border: none;
  ::placeholder {
    font-size: 14px;
    padding-left: 4px;
  }
`

const FullScreenModalOverlay = styled("div")`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (min-width: 600px) {
    align-items: center;
    justify-content: center;
  }
`;

const FullScreenModalSheet = styled("div")`
  width: 100%;
  height: 95vh;
  background: #ffffff;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1);
  box-shadow: 0 -8px 40px rgba(0,0,0,0.18);

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  @media (min-width: 600px) {
    width: 1100px;
    height: 90vh;
    border-radius: 20px;
    animation: scaleIn 0.25s cubic-bezier(0.32, 0.72, 0, 1);

    @keyframes scaleIn {
      from { opacity: 0; transform: scale(0.96) translateY(8px); }
      to { opacity: 1; transform: scale(1) translateY(0); }
    }
  }
`;

const ModalHeader = styled("div")`
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #eeeeee;
  padding: 0;
`;

const ModalDragHandle = styled("div")`
  width: 40px;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin: 12px auto 0;

  @media (min-width: 600px) {
    display: none;
  }
`;

const ModalHeaderContent = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 16px;
`;

const ModalHeaderLeft = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ModalTitle = styled("h2")`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.3px;
`;

const ModalSubtitle = styled("p")`
  margin: 0;
  font-size: 14px;
  color: #888;
  font-weight: 400;
`;

const CloseButton = styled("button")`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;

  &:hover {
    background: #ebebeb;
  }

  ion-icon {
    font-size: 20px;
    color: #555;
  }
`;

const ModalScrollBody = styled("div")`
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 0;
  -webkit-overflow-scrolling: touch;
`;

const ModalFooter = styled("div")`
  flex-shrink: 0;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  padding: 16px 20px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom));
  display: flex;
  gap: 12px;
`;

const FooterButton = styled(IonButton)`
  flex: 1;
  --border-radius: 12px;
  --padding-top: 14px;
  --padding-bottom: 14px;
  height: 52px;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const SectionCard = styled("div")`
  background: #f8f9fb;
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #ececec;
`;

const FieldLabel = styled("label")`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const StyledInput = styled("input")<{disabled?: boolean}>`
  width: 100%;
  box-sizing: border-box;
  height: 52px;
  border-radius: 10px;
  background-color: ${p => p.disabled ? '#f0f0f0' : '#ffffff'};
  outline: none;
  border: 1.5px solid ${p => p.disabled ? '#e0e0e0' : '#d0d8e8'};
  padding: 0 14px;
  font-size: 16px;
  color: ${p => p.disabled ? '#aaa' : '#1a1a1a'};
  transition: border-color 0.15s;
  cursor: ${p => p.disabled ? 'not-allowed' : 'text'};

  &:focus {
    border-color: ${p => p.disabled ? '#e0e0e0' : '#3880ff'};
    box-shadow: ${p => p.disabled ? 'none' : '0 0 0 3px rgba(56, 128, 255, 0.12)'};
  }
`;

const StyledSelect = styled("select")<{disabled?: boolean}>`
  width: 100%;
  box-sizing: border-box;
  height: 52px;
  border-radius: 10px;
  background-color: ${p => p.disabled ? '#f0f0f0' : '#ffffff'};
  outline: none;
  border: 1.5px solid ${p => p.disabled ? '#e0e0e0' : '#d0d8e8'};
  padding: 0 14px;
  font-size: 16px;
  color: ${p => p.disabled ? '#aaa' : '#1a1a1a'};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  appearance: auto;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${p => p.disabled ? '#e0e0e0' : '#3880ff'};
  }
`;

const ErrorText = styled("span")`
  display: block;
  font-size: 12px;
  color: #e03131;
  margin-top: 5px;
  margin-left: 2px;
`;

const PageWrapper = styled("div")`
  min-height: 100vh;
  background: #f4f6f9;
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled("div")`
  background: rgb(182, 217, 238);
  padding: 24px 24px 20px;
  color: white;
`;

const PageTitle = styled("h1")`
  margin: 0 0 10px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.4px;
  color: #1a3a4a;
`;

const WelcomeText = styled("p")`
  margin: 0 0 6px;
  font-size: 18px;
  color: #2a4a5a;
  font-weight: 400;

  strong {
    font-weight: 700;
    color: #1a3a4a;
  }
`;

const SubText = styled("p")`
  margin: 0;
  font-size: 14px;
  color: #4a6a7a;
`;

const PageBody = styled("div")`
  flex: 1;
  padding: 24px 16px 40px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
`;

const SectionHeader = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding: 0 4px;
`;

const SectionTitle = styled("h2")`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.2px;
`;

const SectionCount = styled("span")`
  font-size: 13px;
  color: #999;
  font-weight: 500;
`;

const GuestListCard = styled("div")`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
  overflow: hidden;
  margin-bottom: 24px;

  /* Style group name rows */
  ion-item {
    --background: transparent;
    --inner-padding-end: 16px;
  }
`;

const FinishButton = styled(IonButton)`
  --border-radius: 14px;
  --padding-top: 18px;
  --padding-bottom: 18px;
  height: 56px;
  font-size: 17px;
  font-weight: 600;
  width: 100%;
  margin: 0;
`;

const RegistrationSelectWrapper = styled("div")`
  margin-bottom: 16px;
`;

const RegistrationSelectLabel = styled("div")`
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
`;

const EditToggleRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  margin-bottom: 16px;
  padding: 8px 14px;
  background: #fff8e1;
  border-radius: 10px;
  border: 1px solid #ffe082;
`;

const EditToggleLabel = styled("span")`
  font-size: 14px;
  color: #795548;
  font-weight: 500;
`;

const Purchaser: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams();
  const { apiUrl } = getConfig()

  const storeguestUrl = `${apiUrl}/storeguest`

  const eventcode = params.eventcode
  const eventguid = params.id

  const [toastState, setToastState] = useState({display: false, goback: false, message: ''})
  const [groupName, setGroupName] = useState('')
  const [groupModal, setGroupModal] = React.useState({active: false, groupName: '', groupId: ""})
  const [messageModal, setMessageModal] = React.useState({active: false, message: '', title: ''})
  const [guestModal, setGuestModal] = React.useState({active: false, groupName: '', guestId: "", guestName: ""})
  const [registrationId, setRegistrationId] = React.useState('')
  const [guestDefaultData, setGuestDefaultData] = React.useState({})
  const [fieldListData, setFieldListData] = React.useState({guestlist: [], guestfieldslist:[], guestlookuplist: [], selectedregistrationid: null})
  const [overrideEdit, setOverrideEdit] = React.useState(true)
  const [currentGuest, setCurrentGuest] = React.useState({guestid: 0})
  const [storeGroups, setStoreGroups] = React.useState([])
  const [purchasertitle, setPurchasertitle] = React.useState('')
  const [purchasername, setPurchasername] = React.useState('')
  const [storeurl, setStoreUrl] = React.useState('')
  const [errorData, setErrorData] = React.useState([])

  const emailRegex =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

  useEffect(() => {
    if (registrationId === 'add_new') {
      getStoreGuestData(currentGuest.guestid, true)
    }
    else if (registrationId) {
      getStoreGuestData(currentGuest.guestid, false)
    }
  }, [registrationId])

  useEffect(()=> {
    const getPurchaserData = async () => {
      const purchaserUrl = `${apiUrl}/purchaserregistration/` + eventguid
      const {data} = await axios.get(purchaserUrl)
      const parsedData = JSON.parse(data.jsonString)
      console.log(parsedData)
      localStorage.setItem('tokenstore', parsedData.token)
      setStoreGroups(parsedData?.groups)
      setPurchasertitle(parsedData?.purchasertitle)
      setPurchasername(parsedData?.purchasername)
      setStoreUrl(parsedData?.storeurl)
    }

    getPurchaserData()
  }, [])

  useEffect(() => {
    setOverrideEdit(true)
    setRegistrationId('')
    setGuestDefaultData({})
    setFieldListData({guestlist: [], guestfieldslist:[], guestlookuplist: [], selectedregistrationid: null})
  }, [guestModal.active === false])

  useEffect(() => {
    var newValues = {}
    fieldListData.guestfieldslist.map(field => {
      const fieldValue = "fieldvalue" + field.fieldcontrol.toString()
      if  (field.fieldcontrol == 1) {
        newValues[field.fieldid] = field[fieldValue] != null ? field[fieldValue] ? 'Yes' : 'No' : 'No'
      }
      else {
        newValues[field.fieldid] = field[fieldValue] != null ? field[fieldValue] : ''
      }
    })
    setGuestDefaultData(newValues)
  }, [fieldListData.guestfieldslist])

  const getGroupItems = () => {
    if (!storeGroups || storeGroups.length === 0) return null

    return storeGroups.map((storeGuest, groupIndex) => {
      if (!storeGuest?.groupname?.length) return null

      const isLastGroup = groupIndex === storeGroups.length - 1

      return (
          <GroupBlock key={storeGuest.groupid} style={{borderBottom: isLastGroup ? 'none' : '1px solid #f0f0f0'}}>
            <GroupHeaderRow
                onClick={() => renameGroup(storeGuest)}
                style={{cursor: storeGuest.allowchangegroupname ? 'pointer' : 'default'}}
            >
              <GroupNameText>{storeGuest.groupname}</GroupNameText>
              {storeGuest.allowchangegroupname && (
                  <IonIcon icon={createOutline} style={{width: 16, height: 16, color: '#aaa', flexShrink: 0}} />
              )}
            </GroupHeaderRow>

            {storeGuest.guests?.map((guest, guestIndex) => {
              const isLastGuest = guestIndex === storeGuest.guests.length - 1
              return (
                  <GuestRow
                      key={guest.guestid}
                      onClick={() => manageGuest(guest, storeGuest)}
                      style={{borderBottom: isLastGuest ? 'none' : '1px solid #f7f7f7'}}
                  >
                    <GuestIconWrap style={{background: guest?.guestislocked ? '#f5f5f5' : '#eef3ff'}}>
                      <IonIcon
                          icon={guest?.guestislocked ? lockClosed : createOutline}
                          style={{width: 15, height: 15, color: guest?.guestislocked ? '#aaa' : '#3880ff'}}
                      />
                    </GuestIconWrap>
                    <GuestNameText>{guest.guestname}</GuestNameText>
                    <GuestChevron>›</GuestChevron>
                  </GuestRow>
              )
            })}
          </GroupBlock>
      )
    })
  }

  const putStoreGuestData = async (body) => {
    const token = localStorage.getItem('tokenstore')
    const storeguestUrl = `${apiUrl}/storeguest`

    const {data} = await axios.put(storeguestUrl, body,{headers: {
        'Authorization': `Bearer ${token}`
      }})
    const parsedData = JSON.parse(data.jsonString)
    if (parsedData.status === 'expired') {
      localStorage.removeItem("tokenstore")
      navigate(`/${eventcode}/event`);
    }
    else if (parsedData.status === 'success') {
      console.log("SUCCESS")
    }
  }

  const getStoreGroups = async () => {
    const token = localStorage.getItem('tokenstore')

    const {data} = await axios.get(storeguestUrl, {headers: {
        'Authorization': `Bearer ${token}`
      }})
    const parsedData = JSON.parse(data.jsonString)
    console.log(parsedData)
    if (parsedData.status === 'expired') {
      localStorage.removeItem("tokenstore")
      navigate(`/${eventcode}/event`);
    }
    else if (parsedData.status === 'success') {
      setStoreGroups(parsedData?.groups)
    }
  }

  const renameGroup = (guest) => {
    if (guest.allowchangegroupname) {
      setGroupName(guest.groupname)
      setGroupModal({active: true, groupName: guest.groupname, groupId: guest.groupid})
    }
  }

  const manageGuest = (guest, storeguest) => {
    setCurrentGuest(guest)
    getStoreGuestData(guest.guestid, false)
    setGuestModal({active: true, groupName: storeguest?.groupname, guestId: guest.guestid, guestName: guest.guestname})
  }

  const getStoreGuestData = async (storeguestid, addNew) => {
    if (storeguestid) {
      var body = {"guestid": storeguestid}

      if (addNew) {
        body['registrationid'] = -1
      }
      else if (Number(registrationId) >= 0) {
        body['registrationid'] = Number(registrationId)
      }

      const token = localStorage.getItem('tokenstore')
      const storeguestUrl = `${apiUrl}/storeguest`

      const {data} = await axios.post(storeguestUrl, body,{headers: {
          'Authorization': `Bearer ${token}`
        }})
      const parsedData = JSON.parse(data.jsonString)

      if (parsedData.status === 'expired') {
        localStorage.removeItem("tokenstore")
        navigate(`/${eventcode}/event`);
      }
      else if (parsedData.status === 'success') {
        var copyDataObject = {...parsedData}
        const copyGuestFieldsList = parsedData.guestfieldslist.map(guestFL => {
          return {...guestFL, "overridereadonly": guestFL.fieldreadonly}
        })
        copyDataObject['guestfieldslist'] = copyGuestFieldsList

        if (!registrationId) {
          setRegistrationId(copyDataObject.selectedregistrationid)
        }
        console.log(copyDataObject)
        setFieldListData(copyDataObject)
      }
    }
  }

  const renameGroupText = async () => {
    const body = {
      "groupid": groupModal.groupId,
      "groupname": groupName
    }
    const token = localStorage.getItem('tokenstore')

    const groupRenameUrl = `${apiUrl}/storeguestgroupname`
    const {data} = await axios.put(groupRenameUrl, body, {headers: {
        'Authorization': `Bearer ${token}`
      }})
    const parsedData = JSON.parse(data.jsonString)
    if (parsedData.status === 'expired') {
      localStorage.removeItem("tokenstore")
      navigate(`/${eventcode}/event`);
    }
    else if (parsedData.status === 'failure') {
      setMessageModal({active: true, message: parsedData.message, title: 'Error Saving Group Name'})
    }
    else {
      let storeGuest = storeGroups.find(
          (item) => item.groupid === groupModal.groupId
      )
      storeGuest.groupname = groupName
      setGroupModal({active: false, groupName: '', groupId:''})
    }
  }

  const getGroupRenameModal = () => {
    if (!groupModal.active) return <></>
    return (
        <FullScreenModalOverlay>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            padding: '28px 24px 24px',
            width: '100%',
            maxWidth: 440,
            boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            margin: '0 16px',
          }}>
            <div style={{fontSize: 20, fontWeight: 700, color: '#1a1a1a'}}>Rename Your Group</div>
            <div style={{fontSize: 14, color: '#888'}}>{groupModal.groupName}</div>
            <StyledInput
                id="group-name"
                type="text"
                placeholder={groupModal.groupName}
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
            />
            <div style={{display: 'flex', gap: 12, marginTop: 4}}>
              <IonButton
                  expand="block"
                  fill="outline"
                  color="medium"
                  style={{flex: 1, '--border-radius': '12px', height: '52px', margin: 0}}
                  onClick={() => setGroupModal({active: false, groupName: '', groupId: ''})}
              >
                Cancel
              </IonButton>
              <IonButton
                  expand="block"
                  fill="solid"
                  color="primary"
                  style={{flex: 1, '--border-radius': '12px', height: '52px', margin: 0}}
                  onClick={() => renameGroupText()}
              >
                Save
              </IonButton>
            </div>
          </div>
        </FullScreenModalOverlay>
    )
  }

  const getMessageModal = () => {
    if (messageModal.active) {
      return (
          <>
            <IonBackdrop visible={messageModal.active}></IonBackdrop>
            <div id="box" style={{width: '50%', height: '30%'}}>
              <IonGrid>
                <IonRow style={{marginBottom: '20px'}}>
                  <IonCol style={{ display: "flex" }} size="12" className="ion-justify-content-center" >
                    <div style={{ display: "flex", fontSize: '26px', color: 'red' }}>{messageModal.title}</div>
                  </IonCol>
                </IonRow>
                <IonRow style={{marginBottom: '20px'}}>
                  <IonCol style={{display: "flex"}} size="12" className="ion-justify-content-center">
                    <div style={{ display: "flex", fontSize: '18px' }}>{messageModal.message}</div>
                  </IonCol>
                </IonRow>
                <IonRow style={{marginBottom: '20px'}}>
                  <IonCol style={{display: 'flex'}} className="ion-justify-content-center">
                    <IonButton onClick={() => { setMessageModal({active: false, message: '', title: ''})}} size="large" style={{ width: '80%' }} expand="block" fill="solid" color={'primary'}>
                      Ok
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>
          </>
      )
    }
    else {
      return <></>
    }
  }

  const onFinish = () => {
    window.location.href = storeurl
  }

  const getEditIcon = () => {
    if (fieldListData.selectedregistrationid > 0 && registrationId !== 'add_new') {
      return (
          <IonButton
              fill="clear"
              onClick={() => setOverrideEdit(!overrideEdit)}
              style={{
                '--padding-start': '0',
                '--padding-end': '0',
                '--padding-top': '0',
                '--padding-bottom': '0',
                margin: 0,
                width: 'auto',
                minWidth: 'auto'
              }}
          >
            <IonIcon
                slot="icon-only"
                icon={createOutline}
                style={{
                  width: 22,
                  height: 22,
                  color: overrideEdit ? "#d00909" : '#59c75e'
                }}
            />
          </IonButton>
      )
    }
    else {
      return null
    }
  }

  const getFieldOptions = (guestData) => {
    var options = [<></>]

    if (fieldListData && fieldListData?.guestlookuplist && fieldListData?.guestlookuplist.length > 0) {
      const filteredOptions = fieldListData?.guestlookuplist?.filter(guests => guests.lookupfieldid === guestData.fieldid)

      if (filteredOptions && filteredOptions.length > 0) {
        options = filteredOptions.map((guestListDataOption, index) => {
          return (
              <option value={guestListDataOption?.lookupid} key={index}>
                {guestListDataOption?.lookup}
              </option>
          )
        })
      }
    }

    return options
  }

  const submitData = async () => {
    setErrorData([])

    var fieldData = []
    var errorArray = []
    var error = false
    fieldListData?.guestfieldslist?.map((guestData) => {
      const fieldValueString = "fieldvalue" + guestData.fieldcontrol
      var dataToAppend = {"fieldid":guestData.fieldid}
      if (guestData.fieldrequired && !guestDefaultData[guestData.fieldid]) {
        errorArray.push({fieldid: guestData.fieldid, message: guestData.fieldlabel + ' is required.'})
        error = true
      }
      if (guestData.fieldlabel === 'Email') {
        if (guestDefaultData[guestData.fieldid]?.length > 0 && !emailRegex.test(guestDefaultData[guestData.fieldid])) {
          errorArray.push({fieldid: guestData.fieldid, message: guestData.fieldlabel + ' is invalid.'})
          error = true
        }
      }
      if (guestData.fieldcontrol == 1) {
        dataToAppend[fieldValueString] = guestDefaultData[guestData.fieldid] === 'Yes' ? true : false
        fieldData.push(dataToAppend)
      }
      else if (guestDefaultData[guestData.fieldid]) {
        dataToAppend[fieldValueString] = guestDefaultData[guestData.fieldid]
        fieldData.push(dataToAppend)
      }
    })

    if (!error) {
      var body = {"guestfieldslist": fieldData, "guestid": guestModal.guestId}

      if (registrationId !== 'add_new' && Number(registrationId) > 0) {
        body['registrationid'] = Number(registrationId)
      }
      else if (registrationId === 'add_new') {
        body['registrationid'] = -1
      }

      await putStoreGuestData(body)
      getStoreGroups()
      setGuestModal({active: false, groupName: '', guestId: '', guestName: ''})
    }
    else {
      setErrorData(errorArray)
    }
  }

  const updateGuestData = (value, guestData) => {
    const dataObj = guestDefaultData
    var newObject = {...dataObj}
    newObject[guestData.fieldid] = value
    setGuestDefaultData(newObject)

    const item = errorData.find(item => item.fieldid === guestData.fieldid)
    if (item && value) {
      const newError = errorData.filter(removeItem => removeItem.fieldid !== guestData.fieldid)
      setErrorData(newError)
    }
    else if (guestData.fieldrequired && !value) {
      const errorDataCopy = errorData
      errorDataCopy.push({fieldid: guestData.fieldid, message: guestData.fieldlabel + ' is required.'})
      setErrorData(errorDataCopy)
    }
  }

  const getErrorDataObject = (fieldid) => {
    return errorData.find(errorControl5 => errorControl5.fieldid === fieldid)
  }

  const isFieldDisabled = () =>
      overrideEdit && fieldListData.selectedregistrationid > 0 && registrationId !== 'add_new'

  const renderField = (guestData) => {
    const errorCheckObj = getErrorDataObject(guestData.fieldid)
    const disabled = isFieldDisabled()
    const placeholderLabel = guestData.fieldlabel + (guestData.fieldrequired ? ' *' : '')

    const fieldContent = (() => {
      if (guestData.fieldcontrol === 1) {
        return (
            <StyledSelect
                name={guestData.fieldlabel}
                id={guestData.fieldid}
                disabled={disabled}
                required={guestData.fieldrequired}
                value={guestDefaultData[guestData.fieldid]}
                onChange={(e) => updateGuestData(e.target.value, guestData)}
            >
              <option value="" disabled>{placeholderLabel}</option>
              <option value={'Yes'}>Yes</option>
              <option value={'No'}>No</option>
            </StyledSelect>
        )
      }
      else if (guestData.fieldcontrol === 2) {
        return (
            <StyledSelect
                name={guestData.fieldlabel}
                id={guestData.fieldid}
                disabled={disabled}
                required={guestData.fieldrequired}
                value={guestDefaultData[guestData.fieldid]}
                onChange={(e) => updateGuestData(e.target.value, guestData)}
            >
              <option value="" disabled>{placeholderLabel}</option>
              {getFieldOptions(guestData)}
            </StyledSelect>
        )
      }
      else if (guestData.fieldcontrol === 3) {
        return (
            <StyledInput
                type="number"
                id={guestData.fieldid}
                placeholder={placeholderLabel}
                required={guestData.fieldrequired}
                maxLength={guestData.fieldtextlength}
                value={guestDefaultData[guestData.fieldid]}
                onChange={(e) => updateGuestData(e.target.value, guestData)}
                name={guestData.fieldlabel}
                disabled={disabled}
            />
        )
      }
      else if (guestData.fieldcontrol === 4) {
        return (
            <CurrencyInput
                id={guestData.fieldid}
                name={guestData.fieldlabel}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  height: '52px',
                  borderRadius: '10px',
                  textAlign: 'right',
                  color: disabled ? '#aaa' : '#1a1a1a',
                  backgroundColor: disabled ? '#f0f0f0' : '#ffffff',
                  outline: 'none',
                  border: `1.5px solid ${disabled ? '#e0e0e0' : '#d0d8e8'}`,
                  paddingRight: '14px',
                  paddingLeft: '14px',
                  fontSize: '16px',
                  cursor: disabled ? 'not-allowed' : 'text',
                }}
                placeholder={placeholderLabel}
                prefix="$"
                maxLength={guestData.fieldtextlength}
                value={String(guestDefaultData[guestData.fieldid])}
                decimalsLimit={2}
                disabled={disabled}
                onValueChange={(value) => updateGuestData(value, guestData)}
            />
        )
      }
      else if (guestData.fieldcontrol === 5) {
        return (
            <StyledInput
                type="text"
                id={guestData.fieldid}
                placeholder={placeholderLabel}
                required={guestData.fieldrequired}
                minLength={1}
                disabled={disabled}
                maxLength={guestData.fieldtextlength}
                value={guestDefaultData[guestData.fieldid]}
                onChange={(e) => updateGuestData(e.target.value, guestData)}
                name={guestData.fieldlabel}
            />
        )
      }
      return null
    })()

    return (
        <div key={guestData.fieldid} style={{marginBottom: 10}}>
          {fieldContent}
          {errorCheckObj && <ErrorText>{errorCheckObj.message}</ErrorText>}
        </div>
    )
  }

  const getGuestModal = () => {
    if (!guestModal.active) return <></>

    return (
        <FullScreenModalOverlay>
          <FullScreenModalSheet>
            {/* Header */}
            <ModalHeader>
              <ModalDragHandle />
              <ModalHeaderContent>
                <ModalHeaderLeft>
                  <ModalTitle>Edit Guest</ModalTitle>
                  <ModalSubtitle>{guestModal.groupName} · {guestModal.guestName}</ModalSubtitle>
                </ModalHeaderLeft>
                <CloseButton onClick={() => setGuestModal({active: false, groupName: '', guestId: '', guestName: ''})}>
                  <IonIcon icon={closeOutline} />
                </CloseButton>
              </ModalHeaderContent>
            </ModalHeader>

            {/* Scrollable Body */}
            <ModalScrollBody>
              {/* Registration selector */}
              <RegistrationSelectWrapper>
                <RegistrationSelectLabel>Select or Add Guest</RegistrationSelectLabel>
                <StyledSelect
                    name="guestlist"
                    style={{width: '100%'}}
                    value={registrationId}
                    onChange={(e) => {
                      console.log(e.currentTarget.value)
                      setRegistrationId(e.currentTarget.value)
                    }}
                >
                  <option value={'add_new'}>+ Add New Guest</option>
                  <option disabled>─────────────────────</option>
                  {fieldListData?.guestlist?.map((guestmapList, index) => (
                      <option value={guestmapList.existingguestid} key={index}>
                        {guestmapList.existingguestname}
                      </option>
                  ))}
                </StyledSelect>
              </RegistrationSelectWrapper>

              {/* Edit locked/unlocked notice */}
              {fieldListData.selectedregistrationid > 0 && registrationId !== 'add_new' && (
                  isFieldDisabled() ? (
                      <EditToggleRow>
                        {getEditIcon()}
                        <EditToggleLabel>Fields are locked. Tap the edit icon to unlock.</EditToggleLabel>
                      </EditToggleRow>
                  ) : (
                      <EditToggleRow>
                        {getEditIcon()}
                        <EditToggleLabel>Fields are unlocked. Tap the edit icon to lock.</EditToggleLabel>
                      </EditToggleRow>
                  )
              )}

              {/* Fields */}
              {fieldListData?.guestfieldslist?.map(renderField)}

              {/* Bottom padding so content clears the footer */}
              <div style={{height: 20}} />
            </ModalScrollBody>

            {/* Sticky Footer */}
            <ModalFooter>
              <IonButton
                  expand="block"
                  fill="outline"
                  color="medium"
                  style={{flex: 1, '--border-radius': '12px', height: '52px', margin: 0}}
                  onClick={() => setGuestModal({active: false, groupName: '', guestId: '', guestName: ''})}
              >
                Cancel
              </IonButton>
              <IonButton
                  expand="block"
                  fill="solid"
                  color="primary"
                  disabled={errorData?.length !== 0}
                  style={{flex: 1, '--border-radius': '12px', height: '52px', margin: 0}}
                  onClick={() => submitData()}
              >
                Save Changes
              </IonButton>
            </ModalFooter>
          </FullScreenModalSheet>
        </FullScreenModalOverlay>
    )
  }

  return (
      <>
        <IonToast
            isOpen={toastState.display}
            onDidDismiss={() => {
              if (toastState.goback) {
                navigate(-1)
              }
              setToastState({
                display: false,
                goback: false,
                message: ''
              })
            }}
            cssClass={'custom-toast'}
            message={toastState.message}
            duration={3000}
        />

        {getGroupRenameModal()}
        {getGuestModal()}
        {getMessageModal()}

        <PageWrapper>
          {/* Hero Header */}
          <PageHeader>
            <PageTitle>{purchasertitle}</PageTitle>
            <WelcomeText>Welcome back, <strong>{purchasername}</strong></WelcomeText>
            <SubText>Assign guests to each of your tickets below</SubText>
          </PageHeader>

          {/* Guest List */}
          <PageBody>
            <SectionHeader>
              <SectionTitle>Your Guests</SectionTitle>
              <SectionCount>{storeGroups?.reduce((acc, g) => acc + (g?.guests?.length || 0), 0)} guests</SectionCount>
            </SectionHeader>

            <GuestListCard>
              {getGroupItems()}
            </GuestListCard>

            <FinishButton
                expand="block"
                fill="solid"
                color="primary"
                onClick={() => onFinish()}
            >
              Finish
            </FinishButton>
          </PageBody>
        </PageWrapper>
      </>
  )
}

export default Purchaser

const GroupBlock = styled("div")`
  padding: 0;
`;

const GroupHeaderRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px 10px;
  background: #fafafa;
  gap: 8px;
`;

const GroupNameText = styled("span")`
  font-size: 12px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const GuestRow = styled("div")`
  display: flex;
  align-items: center;
  padding: 14px 18px;
  cursor: pointer;
  transition: background 0.12s;
  gap: 12px;

  &:hover, &:active {
    background: #f5f8ff;
  }
`;

const GuestIconWrap = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GuestNameText = styled("span")`
  flex: 1;
  font-size: 16px;
  color: #1a1a1a;
  font-weight: 500;
`;

const GuestChevron = styled("span")`
  font-size: 22px;
  color: #ccc;
  line-height: 1;
  margin-right: -2px;
`;
